import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users as UsersIcon, Settings as SettingsIcon, LogOut, Moon, Sun, Wrench, Building2, MapPin, Cpu, Boxes, Briefcase, CalendarClock, HardHat, CalendarCheck, Phone, Repeat, BarChart3, Clock, ClipboardCheck, History, TrendingUp, Mail, ServerCog, ShieldCheck, ListChecks } from "lucide-react";
import { APP_VERSION } from "@/version";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useCompany } from "@/context/CompanyContext";
import { API_BASE } from "@/lib/api";
import RoleBadge from "@/components/RoleBadge";

const NAV_SECTIONS = [
  {
    label: null,
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, permission: "dashboard.view", hideForRoles: ["engineer"], testId: "nav-dashboard", end: true },
    ],
  },
  {
    label: "My Work",
    items: [
      { to: "/my/jobs", label: "My Jobs", icon: HardHat, permission: "job.execute", testId: "nav-my-jobs" },
    ],
  },
  {
    label: "Directory",
    items: [
      { to: "/customers", label: "Customers", icon: Building2, permission: "customer.read", testId: "nav-customers" },
      { to: "/sites", label: "Sites", icon: MapPin, permission: "site.read", testId: "nav-sites" },
      { to: "/machines", label: "Machines", icon: Cpu, permission: "machine.read", testId: "nav-machines" },
    ],
  },
  {
    label: "Jobs",
    items: [
      { to: "/dispatch", label: "Dispatch Board", icon: CalendarClock, permission: "dispatch.read", testId: "nav-dispatch" },
      { to: "/jobs", label: "All Jobs", icon: Briefcase, permission: "job.read", testId: "nav-jobs" },
    ],
  },
  {
    label: "Scheduling",
    items: [
      { to: "/scheduling/due", label: "Due Soon", icon: CalendarCheck, permission: "schedule.read", testId: "nav-due-soon" },
      { to: "/scheduling/call-to-book", label: "Call to Book", icon: Phone, permission: "schedule.read", testId: "nav-call-to-book" },
      { to: "/scheduling/schedules", label: "Service Schedules", icon: Repeat, permission: "schedule.read", testId: "nav-schedules" },
    ],
  },
  {
    label: "Reports",
    items: [
      { to: "/reports/engineers", label: "Engineers", icon: BarChart3, permission: "report.read", testId: "nav-report-engineers" },
      { to: "/reports/time-on-site", label: "Time on Site", icon: Clock, permission: "report.read", testId: "nav-report-tos" },
      { to: "/reports/actions-outstanding", label: "Actions Outstanding", icon: ClipboardCheck, permission: "report.read", testId: "nav-report-actions" },
      { to: "/reports/customer-history", label: "Customer History", icon: History, permission: "report.read", testId: "nav-report-customer-history" },
      { to: "/reports/machines-frequency", label: "Machine Service Freq.", icon: TrendingUp, permission: "report.read", testId: "nav-report-machines-freq" },
    ],
  },
  {
    label: "Admin",
    items: [
      { to: "/users", label: "Users", icon: UsersIcon, permission: "users.read", testId: "nav-users" },
    ],
  },
  {
    label: "Settings",
    items: [
      { to: "/machine-types", label: "Machine Types", icon: Boxes, permission: "machine_type.read", testId: "nav-machine-types" },
      { to: "/settings/contact-roles", label: "Contact Roles", icon: UsersIcon, permission: "settings.contact_roles.read", testId: "nav-contact-roles" },
      { to: "/settings/roles", label: "Roles & Permissions", icon: ShieldCheck, permission: "settings.roles.read", testId: "nav-roles" },
      { to: "/settings/scheduling", label: "Scheduling Defaults", icon: CalendarClock, permission: "settings.scheduling.read", testId: "nav-scheduling-defaults" },
      { to: "/settings/checklist-defaults", label: "Checklist Defaults", icon: ListChecks, permission: "settings.checklist.read", testId: "nav-checklist-defaults" },
      { to: "/settings/branding", label: "Branding", icon: SettingsIcon, permission: "settings.branding.write", testId: "nav-branding" },
      { to: "/settings/company", label: "Company Profile", icon: Building2, permission: "settings.read", testId: "nav-company-profile" },
      { to: "/settings/templates", label: "Email & PDF Templates", icon: Mail, permission: "settings.templates.read", testId: "nav-templates" },
      { to: "/settings/smtp", label: "SMTP", icon: ServerCog, permission: "settings.smtp.read", testId: "nav-smtp" },
      { to: "/settings/security", label: "Security", icon: ShieldCheck, permission: "settings.security.read", testId: "nav-security" },
      { to: "/settings", label: "Settings", icon: SettingsIcon, permission: "settings.read", testId: "nav-settings" },
    ],
  },
];

function initials(name) {
  if (!name) return "?";
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase())
    .join("");
}

export default function Sidebar() {
  const { user, hasPermission, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const { company } = useCompany();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const logoUrl = company.logo_file_id
    ? `${API_BASE}/settings/company/logo/public?v=${company.logo_file_id}`
    : null;

  return (
    <aside
      data-testid="app-sidebar"
      className="fixed left-0 top-0 bottom-0 w-56 bg-background border-r border-border flex flex-col z-40"
    >
      <div className="h-14 border-b border-border flex items-center px-4 gap-2">
        {logoUrl ? (
          <img src={logoUrl} alt={company.name} className="h-7 w-7 object-contain rounded-sm" data-testid="sidebar-company-logo" />
        ) : (
          <div className="h-7 w-7 rounded-sm bg-primary/10 text-primary flex items-center justify-center">
            <Wrench className="h-4 w-4" />
          </div>
        )}
        <div className="flex flex-col leading-tight min-w-0">
          <span className="text-sm font-bold tracking-tight truncate" data-testid="sidebar-company-name">{company.name}</span>
          <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Ops Console
          </span>
        </div>
      </div>

      <nav className="flex-1 p-3 space-y-4 overflow-y-auto">
        {NAV_SECTIONS.map((section, idx) => {
          const items = section.items.filter((n) => hasPermission(n.permission) && !(n.hideForRoles || []).includes(user?.role));
          if (items.length === 0) return null;
          return (
            <div key={idx} className="space-y-1">
              {section.label && (
                <div className="px-3 pt-1 pb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  {section.label}
                </div>
              )}
              {items.map((n) => {
                const Icon = n.icon;
                return (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.end}
                    data-testid={n.testId}
                    className={({ isActive }) =>
                      [
                        "flex items-center gap-3 px-3 py-2 text-sm rounded-sm transition-colors",
                        isActive
                          ? "bg-primary/10 text-primary border-l-2 border-primary font-medium pl-[10px]"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground",
                      ].join(" ")
                    }
                  >
                    <Icon className="h-4 w-4" />
                    <span>{n.label}</span>
                  </NavLink>
                );
              })}
            </div>
          );
        })}
      </nav>

      <div className="mt-auto p-3 border-t border-border space-y-2">
        <div className="flex items-center gap-2 px-2 py-1.5">
          <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center text-xs font-bold">
            {initials(user?.full_name)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium truncate" data-testid="sidebar-user-name">
              {user?.full_name}
            </div>
            <div className="mt-0.5"><RoleBadge role={user?.role} /></div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={toggle}
            data-testid="theme-toggle"
            className="flex-1 inline-flex items-center justify-center gap-2 h-8 px-2 text-xs rounded-sm border border-border hover:bg-muted transition-colors"
          >
            {theme === "dark" ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
            <span>{theme === "dark" ? "Light" : "Dark"}</span>
          </button>
          <button
            type="button"
            onClick={handleLogout}
            data-testid="logout-button"
            className="flex-1 inline-flex items-center justify-center gap-2 h-8 px-2 text-xs rounded-sm border border-border hover:bg-muted transition-colors"
          >
            <LogOut className="h-3.5 w-3.5" />
            <span>Logout</span>
          </button>
        </div>
        <div
          className="pt-1 text-center text-[10px] text-muted-foreground/70 tracking-wide"
          data-testid="app-version-display-sidebar"
        >
          v{APP_VERSION}
        </div>
      </div>
    </aside>
  );
}
